import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Pets as PetsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon,
  History as HistoryIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from '@mui/icons-material';
import { Animal } from '../../services/animalService';
import proprietaireService, { ProprietaireResponse } from '../../services/proprietaireService';

interface ProprietaireDetailsProps {
  proprietaireId: number;
  onClose: () => void;
  onUpdate?: (proprietaire: ProprietaireResponse) => void;
}

interface ProprietaireDetailResponse extends ProprietaireResponse {
  animaux?: Animal[];
  consultations?: any[];
  rendezVous?: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`proprietaire-tabpanel-${index}`}
      aria-labelledby={`proprietaire-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProprietaireDetails: React.FC<ProprietaireDetailsProps> = ({
  proprietaireId,
  onClose,
  onUpdate
}) => {
  const [proprietaire, setProprietaire] = useState<ProprietaireDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<ProprietaireResponse>>({});

  // Charger les détails du propriétaire
  useEffect(() => {
    const loadProprietaireDetails = async () => {
      try {
        setLoading(true);
        const response = await proprietaireService.getProprietaireById(proprietaireId);
        setProprietaire(response as ProprietaireDetailResponse);
        setEditForm(response);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des détails du propriétaire');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProprietaireDetails();
  }, [proprietaireId]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    if (!proprietaire) return;
    
    try {
      const updated = await proprietaireService.updateProprietaire(proprietaire.id, editForm);
      setProprietaire(updated as ProprietaireDetailResponse);
      setEditing(false);
      onUpdate?.(updated);
    } catch (err) {
      setError('Erreur lors de la mise à jour du propriétaire');
      console.error(err);
    }
  };

  const handleCancel = () => {
    setEditForm(proprietaire || {});
    setEditing(false);
  };

  const handleFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const calculateAge = (dateNaissance: string) => {
    if (!dateNaissance) return 'Âge inconnu';
    
    const birthDate = new Date(dateNaissance);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age === 0) {
      const months = today.getMonth() - birthDate.getMonth() + 
        (12 * (today.getFullYear() - birthDate.getFullYear()));
      return months > 0 ? `${months} mois` : 'Nouveau-né';
    }
    
    return `${age} an${age > 1 ? 's' : ''}`;
  };

  const getSpecieColor = (espece: string) => {
    const colors: Record<string, string> = {
      'Chien': '#FF9800',
      'Chat': '#9C27B0',
      'Lapin': '#4CAF50',
      'Oiseau': '#2196F3',
      'Poisson': '#00BCD4',
      'Reptile': '#795548',
      'Hamster': '#FF5722',
      'Furet': '#607D8B',
      'Cochon d\'Inde': '#FFC107',
    };
    return colors[espece] || '#757575';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proprietaire) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Propriétaire non trouvé'}</Alert>
        <Button onClick={onClose} sx={{ mt: 2 }}>Fermer</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* En-tête avec informations principales */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1">
                  {proprietaire.prenom} {proprietaire.nom}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Propriétaire depuis {new Date(proprietaire.created_at || '').toLocaleDateString('fr-FR')}
                </Typography>
                <Chip 
                  label={proprietaire.user?.veterinaire_selectionne_nom || 'Aucun vétérinaire assigné'} 
                  color="primary" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
            <Box>
              {!editing ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Modifier
                </Button>
              ) : (
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    color="primary"
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                  >
                    Annuler
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {/* Informations de contact */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon color="action" />
                <Typography variant="body1">
                  {editing ? (
                    <TextField
                      value={editForm.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    proprietaire.email
                  )}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PhoneIcon color="action" />
                <Typography variant="body1">
                  {editing ? (
                    <TextField
                      value={editForm.telephone || ''}
                      onChange={(e) => handleFormChange('telephone', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    proprietaire.telephone
                  )}
                </Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <LocationIcon color="action" sx={{ mt: 0.5 }} />
                <Typography variant="body1">
                  {editing ? (
                    <Box>
                      <TextField
                        label="Adresse"
                        value={editForm.adresse || ''}
                        onChange={(e) => handleFormChange('adresse', e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <Box display="flex" gap={1}>
                        <TextField
                          label="Code postal"
                          value={editForm.code_postal || ''}
                          onChange={(e) => handleFormChange('code_postal', e.target.value)}
                          size="small"
                          sx={{ width: 120 }}
                        />
                        <TextField
                          label="Ville"
                          value={editForm.ville || ''}
                          onChange={(e) => handleFormChange('ville', e.target.value)}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    `${proprietaire.adresse || 'Adresse non renseignée'}, ${proprietaire.code_postal || ''} ${proprietaire.ville || ''}`
                  )}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Statistiques
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {proprietaire.animaux?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Animal{proprietaire.animaux?.length !== 1 ? 'aux' : ''}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="secondary">
                    0
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Consultations
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    0
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    RDV à venir
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Onglets pour organiser les informations */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Animaux" icon={<PetsIcon />} iconPosition="start" />
            <Tab label="Historique" icon={<HistoryIcon />} iconPosition="start" />
            <Tab label="Rendez-vous" icon={<CalendarIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Onglet Animaux */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Animaux du propriétaire ({proprietaire.animaux?.length || 0})
            </Typography>
            
            {proprietaire.animaux && proprietaire.animaux.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Photo</TableCell>
                      <TableCell>Nom</TableCell>
                      <TableCell>Espèce</TableCell>
                      <TableCell>Race</TableCell>
                      <TableCell>Sexe</TableCell>
                      <TableCell>Âge</TableCell>
                      <TableCell>Poids</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {proprietaire.animaux.map((animal) => (
                      <TableRow key={animal.id} hover>
                        <TableCell>
                          {animal.photo_url ? (
                            <Avatar
                              src={animal.photo_url}
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 40, height: 40, bgcolor: getSpecieColor(animal.espece) }}>
                              <PetsIcon />
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {animal.nom}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={animal.espece} 
                            size="small" 
                            sx={{ bgcolor: getSpecieColor(animal.espece), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{animal.race || '-'}</TableCell>
                        <TableCell>
                          {animal.sexe === 'M' ? (
                            <MaleIcon color="primary" />
                          ) : (
                            <FemaleIcon color="secondary" />
                          )}
                        </TableCell>
                        <TableCell>
                          {animal.date_naissance ? calculateAge(animal.date_naissance) : '-'}
                        </TableCell>
                        <TableCell>
                          {animal.poids ? `${animal.poids} kg` : '-'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Voir les détails">
                            <IconButton size="small">
                              <MedicalIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <PetsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Aucun animal enregistré
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ce propriétaire n'a pas encore d'animaux enregistrés
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Onglet Historique */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Historique des consultations
            </Typography>
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Aucun historique disponible
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Les consultations apparaîtront ici une fois enregistrées
              </Typography>
            </Box>
          </Box>
        </TabPanel>

        {/* Onglet Rendez-vous */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Rendez-vous à venir
            </Typography>
            <Box textAlign="center" py={4}>
              <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Aucun rendez-vous programmé
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Les rendez-vous apparaîtront ici une fois planifiés
              </Typography>
            </Box>
          </Box>
        </TabPanel>
      </Card>

      {/* Actions en bas */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button variant="outlined" onClick={onClose}>
          Fermer
        </Button>
      </Box>
    </Box>
  );
};

export default ProprietaireDetails;
